import { useEffect, useState } from "react";
import api, { money, fmtDate, apiErr } from "@/lib/api";
import { useActivity } from "@/context/ActivityContext";
import { toast } from "sonner";
import Modal from "@/components/Modal";
import ConfirmDialog from "@/components/ConfirmDialog";
import { InlineError } from "@/components/InlineError";
import StatusBadge from "@/components/StatusBadge";
import Pagination from "@/components/Pagination";
import { Plus, FileText, Pencil, Trash2 } from "lucide-react";

const empty = { name: "", kind: "customer", phone: "", email: "", address: "", credit_limit: "" };

export default function Parties() {
  const { current } = useActivity();
  const [tab, setTab] = useState("customer");
  const [rows, setRows] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(empty);
  const [stmt, setStmt] = useState(null);
  const [saving, setSaving] = useState(false);
  const [saveErr, setSaveErr] = useState("");
  const [confirmSave, setConfirmSave] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [delTarget, setDelTarget] = useState(null);
  const [page, setPage] = useState(1);

  const load = async () => { const { data } = await api.get(`/parties?kind=${tab}${current ? `&activity_id=${current.id}` : ""}`); setRows(data); setPage(1); };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [tab, current?.id]);

  const openNew = () => { setSaveErr(""); setEditingId(null); setForm({ ...empty, kind: tab }); setOpen(true); };
  const openEdit = (p) => { setSaveErr(""); setEditingId(p.id); setForm({ name: p.name || "", kind: p.kind || tab, phone: p.phone || "", email: p.email || "", address: p.address || "", credit_limit: p.credit_limit || "" }); setOpen(true); };
  const delParty = async () => {
    try { await api.delete(`/parties/${delTarget.id}${current ? `?activity_id=${current.id}` : ""}`); toast.success("Supprimé"); setDelTarget(null); load(); }
    catch (e) { toast.error(apiErr(e.response?.data?.detail)); setDelTarget(null); }
  };
  const askSave = () => {
    setSaveErr("");
    if (!form.name.trim()) { setSaveErr("Le nom est obligatoire."); return; }
    setConfirmSave(true);
  };
  const save = async () => {
    setSaving(true); setSaveErr("");
    try {
      if (editingId) await api.patch(`/parties/${editingId}${current ? `?activity_id=${current.id}` : ""}`, { ...form, credit_limit: Number(form.credit_limit || 0) });
      else await api.post("/parties", { ...form, activity_id: current?.id, credit_limit: Number(form.credit_limit || 0) });
      toast.success("Enregistré"); setConfirmSave(false); setOpen(false);
      if (form.kind === tab) load(); else setTab(form.kind);
    } catch (e) { setConfirmSave(false); setSaveErr(apiErr(e.response?.data?.detail, e)); }
    finally { setSaving(false); }
  };
  const viewStatement = async (p) => { const { data } = await api.get(`/parties/${p.id}/statement${current ? `?activity_id=${current.id}` : ""}`); setStmt(data); };

  return (
    <div className="space-y-5">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div><h1 className="text-2xl md:text-3xl font-bold font-head text-[#16324F] tracking-tight">Clients & Fournisseurs</h1>
          <p className="text-sm text-[#64748B] mt-1">Répertoire · code d'identification · plafonds de crédit · relevés</p></div>
        <button onClick={openNew} className="btn-primary" data-testid="new-party-btn"><Plus className="w-4 h-4" /> {tab === "customer" ? "Nouveau client" : "Nouveau fournisseur"}</button>
      </div>

      <div className="flex gap-1 bg-[#F1F5F9] p-1 rounded-xl w-fit">
        {[["customer", "Clients"], ["supplier", "Fournisseurs"]].map(([k, l]) => (
          <button key={k} onClick={() => setTab(k)} data-testid={`party-tab-${k}`}
            className={`px-4 py-2 rounded-lg text-sm font-semibold transition-all ${tab === k ? "bg-white text-[#2563EB] shadow-sm" : "text-[#64748B] hover:text-[#334155]"}`}>{l}</button>
        ))}
      </div>

      <div className="card md:hidden divide-y divide-[#E2E8F0]" data-testid="parties-cards">
        {rows.slice((page - 1) * 10, page * 10).map((p) => (
          <div key={p.id} className="py-3 first:pt-0" data-testid={`party-card-${p.id}`}>
            <div className="flex items-start justify-between gap-3">
              <div className="min-w-0">
                <div className="font-semibold text-[#0F172A] break-words">{p.name}</div>
                <div className="text-xs text-[#64748B] flex flex-wrap gap-x-2"><span className="font-mono text-[#0E7490]">{p.code || "—"}</span>{p.phone && <span>{p.phone}</span>}</div>
                {p.address && <div className="text-xs text-[#64748B] break-words">{p.address}</div>}
              </div>
              <div className="text-right shrink-0">
                <div className="text-[10px] uppercase text-[#94A3B8]">{p.balance < 0 ? "Avoir" : "Solde dû"}</div>
                <div className={`font-bold whitespace-nowrap ${p.balance > 0 ? "text-[#B45309]" : "text-[#15803D]"}`} data-testid={`party-balance-${p.id}`}>{p.balance < 0 ? `Avoir ${money(-p.balance)}` : money(p.balance)}</div>
              </div>
            </div>
            <div className="mt-2 flex items-center justify-between gap-2 flex-wrap">
              <span className="text-xs text-[#64748B]">Plafond {money(p.credit_limit)}</span>
              <div className="flex gap-1.5">
                {tab === "customer" && <button onClick={() => viewStatement(p)} className="btn-outline !py-1.5 !px-2.5 min-h-[36px]" data-testid={`statement-btn-m-${p.id}`}><FileText className="w-3.5 h-3.5" /> Relevé</button>}
                <button onClick={() => openEdit(p)} className="btn-outline !py-1.5 !px-2.5 min-h-[36px]" data-testid={`edit-party-m-${p.id}`}><Pencil className="w-3.5 h-3.5" /></button>
                <button onClick={() => setDelTarget(p)} className="btn-outline !py-1.5 !px-2.5 min-h-[36px] !text-[#B91C1C]" data-testid={`delete-party-m-${p.id}`}><Trash2 className="w-3.5 h-3.5" /></button>
              </div>
            </div>
          </div>
        ))}
        {rows.length === 0 && <div className="text-center py-10 text-[#94A3B8] text-sm">Aucun enregistrement</div>}
      </div>
      <div className="card overflow-x-auto hidden md:block">
        <table className="w-full"><thead><tr>
          <th className="th">Code</th><th className="th">Nom</th><th className="th">Téléphone</th><th className="th">Adresse</th>
          <th className="th text-right">Plafond crédit</th><th className="th text-right">Solde dû</th><th className="th text-right">Action</th>
        </tr></thead><tbody>
          {rows.slice((page - 1) * 10, page * 10).map((p) => (
            <tr key={p.id} className="row" data-testid={`party-row-${p.id}`}>
              <td className="td font-mono text-xs text-[#0E7490]">{p.code || "—"}</td>
              <td className="td font-medium text-[#0F172A]">{p.name}</td>
              <td className="td">{p.phone || "—"}</td>
              <td className="td text-[#64748B]">{p.address || "—"}</td>
              <td className="td text-right">{money(p.credit_limit)}</td>
              <td className={`td text-right font-semibold ${p.balance > 0 ? "text-[#B45309]" : "text-[#15803D]"}`} data-testid={`party-balance-row-${p.id}`}>{p.balance < 0 ? `Avoir ${money(-p.balance)}` : money(p.balance)}</td>
              <td className="td text-right whitespace-nowrap">
                {tab === "customer" && <button onClick={() => viewStatement(p)} className="btn-outline !py-1.5 !px-2.5 mr-1.5" data-testid={`statement-btn-${p.id}`}><FileText className="w-3.5 h-3.5" /> Relevé</button>}
                <button onClick={() => openEdit(p)} className="btn-outline !py-1.5 !px-2.5 mr-1.5" data-testid={`edit-party-${p.id}`}><Pencil className="w-3.5 h-3.5" /></button>
                <button onClick={() => setDelTarget(p)} className="btn-outline !py-1.5 !px-2.5 !text-[#B91C1C]" data-testid={`delete-party-${p.id}`}><Trash2 className="w-3.5 h-3.5" /></button>
              </td>
            </tr>
          ))}
          {rows.length === 0 && <tr><td colSpan={7} className="td text-center py-10 text-[#94A3B8]">Aucun enregistrement</td></tr>}
        </tbody></table>
      </div>
      <Pagination page={page} setPage={setPage} total={rows.length} />

      <ConfirmDialog open={!!delTarget} danger title="Supprimer le compte" message={`Supprimer « ${delTarget?.name} » ? Cette action est définitive.`} confirmLabel="Supprimer" onConfirm={delParty} onClose={() => setDelTarget(null)} />

      <Modal open={open} onClose={() => setOpen(false)} title={editingId ? (form.kind === "customer" ? "Modifier le client" : "Modifier le fournisseur") : (form.kind === "customer" ? "Nouveau client" : "Nouveau fournisseur")} testId="party-modal"
        footer={<><button className="btn-outline" onClick={() => setOpen(false)}>Annuler</button><button className="btn-primary" onClick={askSave} disabled={saving} data-testid="save-party-btn">Enregistrer</button></>}>
        <div className="space-y-4">
          <div>
            <label className="lbl">Type</label>
            <select className="inp" value={form.kind} onChange={(e) => setForm({ ...form, kind: e.target.value })} data-testid="party-kind-select">
              <option value="customer">Client</option>
              <option value="supplier">Fournisseur</option>
            </select>
          </div>
          <div><label className="lbl">Nom</label><input className="inp" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="party-name-input" /></div>
          <div className="grid grid-cols-2 gap-3">
            <div><label className="lbl">Téléphone</label><input className="inp" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} data-testid="party-phone-input" /></div>
            <div><label className="lbl">E-mail</label><input className="inp" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} /></div>
          </div>
          <div><label className="lbl">Adresse</label><input className="inp" value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} /></div>
          {form.kind === "customer" && <div><label className="lbl">Plafond de crédit</label><input type="number" className="inp" value={form.credit_limit} onChange={(e) => setForm({ ...form, credit_limit: e.target.value })} data-testid="party-credit-input" /></div>}
          <InlineError message={saveErr} title="Enregistrement impossible" />
        </div>
      </Modal>

      <ConfirmDialog open={confirmSave} danger={false} title="Confirmer l'enregistrement" message={`Enregistrer ${form.kind === "customer" ? "le client" : "le fournisseur"} « ${form.name} » ?`} confirmLabel="Valider et enregistrer" loading={saving} onConfirm={save} onClose={() => setConfirmSave(false)} />

      <Modal open={!!stmt} onClose={() => setStmt(null)} title={`Relevé — ${stmt?.party?.name}`} wide testId="statement-modal">
        {stmt && (
          <div className="space-y-4">
            <div className="p-3 rounded-lg bg-[#F8FAFC] border border-[#E2E8F0] flex justify-between">
              <span className="text-sm text-[#64748B]">{stmt.party.balance < 0 ? "Avoir disponible" : "Solde dû actuel"}</span>
              <span className={`font-bold ${stmt.party.balance < 0 ? "text-[#047857]" : "text-[#B45309]"}`} data-testid="party-statement-balance">{stmt.party.balance < 0 ? money(-stmt.party.balance) : money(stmt.party.balance)}</span>
            </div>
            <div><h4 className="text-sm font-semibold mb-2">Ventes</h4>
              <table className="w-full text-sm"><thead><tr><th className="th">N°</th><th className="th">Date</th><th className="th text-right">Total</th><th className="th text-right">Solde</th><th className="th">Statut</th></tr></thead>
                <tbody>{stmt.sales.map((s) => <tr key={s.id} className="row"><td className="td font-mono text-xs">{s.number}</td><td className="td text-xs">{fmtDate(s.created_at)}</td><td className="td text-right">{money(s.total)}</td><td className="td text-right">{money(s.balance)}</td><td className="td"><StatusBadge status={s.status} /></td></tr>)}
                  {stmt.sales.length === 0 && <tr><td colSpan={5} className="td text-center text-[#94A3B8]">Aucune vente</td></tr>}</tbody></table>
            </div>
            <div><h4 className="text-sm font-semibold mb-2">Encaissements</h4>
              <table className="w-full text-sm"><thead><tr><th className="th">Date</th><th className="th">Mode</th><th className="th text-right">Montant</th></tr></thead>
                <tbody>{stmt.payments.map((p) => <tr key={p.id} className="row"><td className="td text-xs">{fmtDate(p.created_at)}</td><td className="td">{p.method}</td><td className="td text-right text-[#15803D]">{money(p.amount)}</td></tr>)}
                  {stmt.payments.length === 0 && <tr><td colSpan={3} className="td text-center text-[#94A3B8]">Aucun encaissement</td></tr>}</tbody></table>
            </div>
          </div>
        )}
      </Modal>
    </div>
  );
}
